import React from 'react';
import { Music, Phone, Mail, MapPin, Facebook, Youtube, ExternalLink } from 'lucide-react';
import { contactData, socialLinks, artistData } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-stone-950 border-t border-stone-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer */}
        <div className="py-16 grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-gold-500 flex items-center justify-center">
                <Music className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-lg">CountrySoulN'Roll</p>
                <p className="text-gold-400 text-xs tracking-widest uppercase">Entertainment</p>
              </div>
            </div>
            <p className="text-stone-400 text-sm leading-relaxed mb-6">
              {artistData.tagline} — Bringing nostalgia-driven country music to the Midwest, one show at a time.
            </p>
            <div className="flex gap-3">
              <a
                href={socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-stone-900 border border-stone-800 flex items-center justify-center hover:bg-blue-500/20 hover:border-blue-500/50 transition-all duration-300 group"
              >
                <Facebook className="w-5 h-5 text-stone-400 group-hover:text-blue-500" />
              </a>
              <a
                href={socialLinks.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg bg-stone-900 border border-stone-800 flex items-center justify-center hover:bg-red-500/20 hover:border-red-500/50 transition-all duration-300 group"
              >
                <Youtube className="w-5 h-5 text-stone-400 group-hover:text-red-500" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: 'About', href: '#about' },
                { name: 'Services', href: '#services' },
                { name: 'Pricing', href: '#pricing' },
                { name: 'Coverage Area', href: '#coverage' },
                { name: 'Watch Performances', href: '#watch' },
                { name: 'Book Now', href: '#contact' }
              ].map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="text-stone-400 hover:text-gold-400 transition-colors text-sm"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${contactData.receptionist.phone}`}
                  className="flex items-start gap-3 text-stone-400 hover:text-gold-400 transition-colors group"
                >
                  <Phone className="w-4 h-4 mt-0.5 text-stone-500 group-hover:text-gold-400" />
                  <div>
                    <p className="text-sm">{contactData.receptionist.name}</p>
                    <p className="text-white">{contactData.receptionist.phone}</p>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href={`tel:${contactData.human.phone}`}
                  className="flex items-start gap-3 text-stone-400 hover:text-green-400 transition-colors group"
                >
                  <Phone className="w-4 h-4 mt-0.5 text-stone-500 group-hover:text-green-400" />
                  <div>
                    <p className="text-sm">{contactData.human.name}</p>
                    <p className="text-white">{contactData.human.phone}</p>
                  </div>
                </a>
              </li>
              <li className="flex items-start gap-3 text-stone-400">
                <MapPin className="w-4 h-4 mt-0.5 text-stone-500" />
                <span className="text-sm">Based in Omaha, Nebraska</span>
              </li>
            </ul>
          </div>

          {/* ASCAP Info */}
          <div>
            <h4 className="text-white font-semibold mb-4">Licensing</h4>
            <div className="bg-stone-900/50 border border-stone-800 rounded-xl p-4">
              <p className="text-gold-400 text-sm font-medium mb-2">ASCAP Licensed</p>
              <p className="text-stone-300 text-sm">License #: {contactData.ascap.licenseNumber}</p>
              <div className="mt-3 pt-3 border-t border-stone-800">
                <p className="text-stone-500 text-xs">Representative:</p>
                <p className="text-stone-400 text-sm">{contactData.ascap.representative}</p>
                <a
                  href={`tel:${contactData.ascap.phone}`}
                  className="text-stone-400 text-sm hover:text-gold-400 transition-colors"
                >
                  {contactData.ascap.phone}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-stone-800 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-stone-500 text-sm">
            © {currentYear} CountrySoulN'Roll Entertainment. All rights reserved.
          </p>
          <p className="text-stone-600 text-xs">
            Alexander Richard • Nebraska Troubadour
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
